import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

/**
 * Forma de função, não `SxProps`: `SxProps` já inclui o caso array, e array de
 * `SxProps` não é um `sx` válido — é o que quebra ao compor duas delas.
 */
type MenuListSx = (theme: Theme) => SystemStyleObject<Theme>;

/**
 * Verde PontuaX — os mesmos tons que o resto do admin já usa (`#79CF62` é o
 * `primary.main` do tema, `#275030` o `primary.dark`, `#e8f5e9` o fundo claro).
 *
 * `GREEN` preenche, `GREEN_DARK` escreve: #79CF62 sobre fundo claro não chega
 * perto do contraste mínimo para texto.
 */
export const GREEN = '#79CF62';
export const GREEN_DARK = '#275030';
export const GREEN_SOFT = '#e8f5e9';

/**
 * Marca o cabeçalho do grupo que GUARDA a rota atual.
 *
 * É uma classe, e não `sx` no próprio cabeçalho, por especificidade: o ladrilho
 * vem de `& .MuiListItemIcon-root` da lista (0,2,0) e venceria o `sx` do
 * componente (0,1,0).
 */
export const SUBMENU_TRAIL_CLASS = 'SubMenuHeader-trail';

const ROW_RADIUS = '10px';
const TILE_SIZE = 30;

/** Lista do acordeão: presa ao trilho vertical, na coluna da gaveta. */
export const railListSx: MenuListSx = theme => ({
    borderLeft: `2px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(2.75),
    marginBottom: 0.5,

    '& .MuiMenuItem-root': {
        paddingLeft: theme.spacing(1.5),
        fontSize: '0.875rem',
    },
    // Ícone do filho menor e mais apagado que o do header.
    '& .MuiListItemIcon-root': {
        minWidth: theme.spacing(4),
        color: 'text.disabled',
        '& .MuiSvgIcon-root': {
            fontSize: '1.15rem',
        },
    },
    // O item ativo marca o trilho, para achar onde se está sem precisar ler a
    // lista inteira.
    '& .RaMenuItemLink-active': {
        fontWeight: 600,
        boxShadow: `inset 2px 0 0 0 ${theme.palette.primary.main}`,
    },
});

/**
 * Lista do painel flutuante: cada item é uma linha com ladrilho de ícone, e o
 * ativo vira pílula violeta.
 *
 * O ladrilho é o que dá ritmo à lista — sem ele os ícones ficam soltos e as
 * linhas, com o espaçamento maior, parecem desalinhadas.
 */
export const panelListSx: MenuListSx = theme => ({
    px: 1,
    py: 0.75,

    '& .MuiMenuItem-root': {
        minHeight: 40,
        paddingLeft: 0.75,
        paddingRight: 1,
        marginBottom: '2px',
        borderRadius: ROW_RADIUS,
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        // O tema pinta 3px de borda em TODO MenuItemLink para marcar o ativo na
        // coluna da gaveta; aqui quem marca é a pílula.
        borderLeft: 'none',
    },

    '& .MuiListItemIcon-root': {
        minWidth: TILE_SIZE,
        width: TILE_SIZE,
        height: TILE_SIZE,
        marginLeft: 0,
        marginRight: 1.25,
        borderRadius: ROW_RADIUS,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
        '& .MuiSvgIcon-root': {
            fontSize: '1.05rem',
        },
    },

    /*
     * Em `Menu/index.tsx` vários submenus aninhados vêm embrulhados num
     * `<MenuItem style={{ display: 'list-item' }}>`. Esse embrulho não é uma
     * linha: se ele receber o visual de linha, a linha de verdade (lá dentro)
     * sai indentada e com o dobro da altura. Aqui ele volta a ser invólucro.
     *
     * O `paddingBlock: 4` é inline no JSX e vence a classe, então sobra como
     * respiro vertical — que é o que ele sempre foi.
     */
    '& .MuiMenuItem-root:has(.MuiMenuItem-root)': {
        padding: 0,
        minHeight: 0,
        marginBottom: 0,
        borderRadius: 0,
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },

    // Grupo que contém a rota atual: a mesma leitura do item ativo, sem a
    // pílula — ela é de quem É a página, não de quem guarda a página.
    [`& .${SUBMENU_TRAIL_CLASS}`]: {
        color: GREEN_DARK,
        fontWeight: 600,
        '& .MuiListItemIcon-root': {
            backgroundColor: GREEN_SOFT,
            color: GREEN_DARK,
        },
    },

    '& .RaMenuItemLink-active': {
        backgroundColor: GREEN_SOFT,
        color: GREEN_DARK,
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: GREEN_SOFT,
        },
        '& .MuiListItemIcon-root': {
            backgroundColor: GREEN,
            // Glifo em verde escuro, não branco: branco sobre #79CF62 fica em
            // 1,9:1 e some.
            color: GREEN_DARK,
        },
        // Seta de "você está aqui", no fim da linha. Vem do CSS porque os
        // filhos são `<MenuItemLink>` escritos em `Menu/index.tsx` — injetar
        // um ícone neles custaria mexer nas ~200 chamadas.
        '&::after': {
            content: '""',
            flex: '0 0 auto',
            width: 6,
            height: 6,
            marginLeft: 1,
            borderTop: '2px solid currentColor',
            borderRight: '2px solid currentColor',
            borderRadius: '1px',
            transform: 'rotate(45deg)',
            opacity: 0.55,
        },
    },
});

